// src/belediye/pages/RedirectedApplicationsPage.js

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
// FAZ-13: izin/kapsam cutover'i
import usePermissions from "../hooks/usePermissions";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  ArrowPathRoundedSquareIcon,
  ChevronUpDownIcon,
  ArrowTopRightOnSquareIcon,
  BuildingOffice2Icon,
  HashtagIcon,
  TagIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import TrDateTime from "../components/utils/TrDateTime";
import { safeText, ensureFirstUpperTr, titleCaseTr } from "../components/utils/textFormat";
import PageHeader from "../components/utils/PageHeader";
// FAZ-97 madde 3 — başvuru çip şeridi burada da çizilir
import BasvuruCipSeridi, {
  KAPSAM_TABS,
} from "../components/applications/BasvuruCipSeridi";
import SearchBox from "../components/utils/SearchBox";
import BottomSheet from "../components/utils/BottomSheet";
// FAZ-99 / Y-3: dar düzen kararı katman sözleşmesinden (bkz. belediye/olcu.js)
import useKatman from "../hooks/useKatman";
import Pagination from "../components/utils/Pagination";

import {
  fetchRedirectedApplicationsPageThunk,
  fetchRedirectedApplicationHistoryThunk,
  clearRedirectedApplications,
  clearAllHistory,
} from "../redux/slices/belediyeForwardLogsSlice";

import api, { withFullscreenLoader } from "../services/belediyeApi";

// Başvurular header/toolbar/tabs/search/popover görünümünü BİREBİR paylaşmak için
import "./ApplicationsPage.css";
import "./ApplicationsPage.mobile.css";
import "./RedirectedApplicationsPage.css";
import "./RedirectedApplicationsPage.mobile.css";

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */

const safeJsonParse = (v) => {
  try { return JSON.parse(v); } catch { return null; }
};

const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const sameId = (a, b) => toStr(a) !== "" && toStr(a) === toStr(b);
const pick = (...vals) => vals.find((v) => v !== null && v !== undefined && v !== "");

const parseList = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
};

// RBAC: tek-birimli kullanıcıda legacy user.unit BOŞ olabilir (birim yalnız
// UnitMembership'te tutulur). Bu durumda AKTİF birime düşeriz:
// localStorage.activeUnitId (BelediyeApp login'de yazar) → yoksa kullanıcının
// TEK aktif üyeliği. Aksi halde myUnitId=null → canNavigate hep false → tüm
// satırlar yanlışlıkla "Birim dışı" etiketlenirdi.
const getUserUnitId = (user) => {
  const legacy = pick(user?.unit?.id, user?.unit_id, user?.unit);
  if (legacy) return legacy;
  const stored = Number(localStorage.getItem("activeUnitId"));
  if (Number.isFinite(stored) && stored > 0) return stored;
  const units = Array.isArray(user?.units)
    ? user.units.filter((u) => u?.is_active !== false)
    : [];
  if (units.length === 1) return pick(units[0]?.unit_id, units[0]?.id);
  return undefined;
};

/* FAZ-99 / Y-3: `MOBILE_MAX_WIDTH = 768` KALDIRILDI — katman sözleşmesi tek
   kaynak (belediye/olcu.js, useKatman). Gerekçe aşağıda, isMobile'ın yanında. */

// Başvurular ile AYNI: sayfa başına 50 kayıt.
const PAGE_SIZE = 50;

// Backend'den gelen özet satır için son hop metni
const summaryHopText = (item) => {
  const from = item?.last_forward_from_unit_name || "—";
  const to   = item?.last_forward_to_unit_name   || "—";
  return `${from} → ${to}`;
};

/* ─────────────────────────────────────────────
   DEBOUNCE HOOK — arama için
───────────────────────────────────────────── */
const useDebounce = (value, delay = 400) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

/* ─────────────────────────────────────────────
   PAGE
───────────────────────────────────────────── */

const RedirectedApplicationsPage = () => {
  const navigate  = useNavigate();
  const params    = useParams();
  const dispatch  = useDispatch();

  const unitSlugParam = pick(
    params?.unitSlug, params?.slug, params?.unit_slug, params?.unit
  );

  const token = localStorage.getItem("accessToken") || localStorage.getItem("token") || "";
  const user  = safeJsonParse(localStorage.getItem("user")) || null;

  // FAZ-13 (3.x): "tum-birim gorunumu" KAPSAM kararidir — uiOn'da
  // /me.scope.mode==="all_units" (rol string'i degil). uiOn=false eski yol.
  const { uiOn, scope: authzScope, can } = usePermissions();

  /* FAZ-97 madde 3 — kapsam çipleri izinden süzülür (K-7). Bu sayfada zaten
     "Yönlendirilenler" aktif; şerit onu işaretli çiziyor. */
  const kapsamCipleri = useMemo(
    () => KAPSAM_TABS.filter((k) => can(k.permission)),
    [can],
  );
  const isPresident = uiOn
    ? authzScope?.mode === "all_units"
    : user?.role === "başkan";
  const myUnitId    = getUserUnitId(user);

  // ── Redux state ────────────────────────────────────────────
  const {
    items,
    count,
    page: reduxPage,
    pageSize,
    listLoading,
    listError,
    historyByApplicationId,
    historyLoadingByApplicationId,
    historyErrorByApplicationId,
  } = useSelector((s) => s.belediyeForwardLogs);

  // ── Local UI state ─────────────────────────────────────────
  /* FAZ-99 / Y-3 — DAR DÜZEN ARTIK KATMANDAN OKUNUYOR.

     Kullanıcı (2026-08-02): "yönlendirilenler sayfası da tablette mobil gibi
     olsun; birimler kısmını yukarı alalım, mobildeki gibi açılsın."

     Eski eşik çıplak `max-width: 768px` idi: iPad mini DİKEY tam 768px
     raporladığı için orada mobil düzen (Birimler yukarıda düğme), iPad Air'de
     (820) masaüstü düzeni (sol sütunda Birimler listesi) çiziliyordu. Aynı
     ailenin iki cihazı iki farklı sayfa gösteriyordu — FAZ-98'de navbar,
     sekme ve filtrede yakalanan 768 tuzağının dördüncü örneği.

     SUNUM ise ikiye ayrılıyor (kullanıcı ayrıca istedi): telefonda alttan
     çekmece, tablette düğmeye ÇIPALI popover — "aşağıdan çekmece değil de
     web'teki filtreler kısmı gibi açılsın". */
  const katmanAdi = useKatman();
  const telefon = katmanAdi === "telefon";
  const isMobile = telefon || katmanAdi === "tablet";

  const [selectedUnitId, setSelectedUnitId] = useState("allUnits");
  const [isUnitsOpen,    setIsUnitsOpen]    = useState(false);
  const openUnits   = useCallback(() => setIsUnitsOpen(true),  []);
  const closeUnits  = useCallback(() => setIsUnitsOpen(false), []);
  const toggleUnits = useCallback(() => setIsUnitsOpen((v) => !v), []);

  const [scope, setScope] = useState("all");
  const [q,     setQ]     = useState("");
  const [page,  setPage]  = useState(1);

  const debouncedQ = useDebounce(q, 400);

  // accordion: { [applicationId]: boolean }
  const [expanded, setExpanded] = useState({});

  // units list (sidebar için)
  const [units,        setUnits]        = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(false);

  // Header yüksekliğini CANLI ölçmek için ref-callback (remount'a dayanıklı).
  // Header artık ana return içinde inline; yine de ref-callback en sağlam yol:
  // düğüm her değiştiğinde eski ResizeObserver kapatılıp yenisi bağlanır →
  // --rb-sticky-h asla bayatlamaz, sidebar başlığın altına kaymaz.
  const stickyRoRef = useRef(null);
  const setStickyEl = useCallback((el) => {
    if (stickyRoRef.current) {
      stickyRoRef.current.disconnect();
      stickyRoRef.current = null;
    }
    if (!el) return;
    const apply = () => {
      const h = el.getBoundingClientRect().height || 0;
      document.documentElement.style.setProperty(
        "--rb-sticky-h",
        `${Math.ceil(h)}px`
      );
    };
    apply();
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(apply);
      ro.observe(el);
      stickyRoRef.current = ro;
    }
  }, []);

  // ── Türetilmiş değerler ────────────────────────────────────
  // Tümü/Gelen/Giden sekmeleri: yalnız FİLTRELEME yapıldığında görünür.
  // Başkan → belirli bir birim seçtiyse (allUnits değil). Birim personeli →
  // kendi birimi zaten örtük seçili olduğundan hep görünür (aynı mantık).
  const showScopeTabs = isPresident ? selectedUnitId !== "allUnits" : true;

  const myUnitSlug = (() => {
    if (unitSlugParam) return unitSlugParam;
    const fromUser = pick(user?.unit?.slug, user?.unit_slug, user?.unitSlug);
    if (fromUser) return fromUser;
    // RBAC: legacy boşsa aktif birim slug'ı (localStorage) veya tek aktif üyelik
    const storedSlug = localStorage.getItem("activeUnitSlug");
    if (storedSlug && storedSlug !== "allUnits") return storedSlug;
    const units = Array.isArray(user?.units)
      ? user.units.filter((u) => u?.is_active !== false)
      : [];
    if (units.length === 1) return pick(units[0]?.unit_slug, units[0]?.slug);
    return undefined;
  })();

  const getUnitName = useCallback(
    (id) => (units || []).find((u) => sameId(u.id, id))?.name || "—",
    [units]
  );

  // ── Auth guard ─────────────────────────────────────────────
  useEffect(() => {
    if (!token || !user) navigate("/giris");
  }, [token, user, navigate]);

  // Eski `resize`/`matchMedia` dinleyicisi KALDIRILDI: useKatman tek kaynak.

  // ── Başkan: allUnits seçiliyse scope all olmalı ─────────────
  useEffect(() => {
    if (!isPresident) return;
    if (selectedUnitId === "allUnits" && scope !== "all") setScope("all");
  }, [isPresident, selectedUnitId, scope]);

  // ── Desktop'a geçince drawer kapat ─────────────────────────
  useEffect(() => {
    if (!isPresident) return;
    if (!isMobile && isUnitsOpen) closeUnits();
  }, [isPresident, isMobile, isUnitsOpen, closeUnits]);

  // ── Drawer açıkken body scroll kilidi + ESC ─────────────────
  useEffect(() => {
    // Kilit yalnız TELEFONDA: tablette panel bir popover, arka sayfa okunur.
    if (!isPresident || !telefon || !isUnitsOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e) => { if (e.key === "Escape") closeUnits(); };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isPresident, telefon, isUnitsOpen, closeUnits]);

  // ── Units listesi (sidebar stats için) ─────────────────────
  useEffect(() => {
    if (!isPresident) return;
    let mounted = true;
    setUnitsLoading(true);
    api
      .get("/common/units/", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => { if (mounted) setUnits(parseList(res.data)); })
      .catch(() => { if (mounted) setUnits([]); })
      .finally(() => { if (mounted) setUnitsLoading(false); });
    return () => { mounted = false; };
  }, [token, isPresident]);

  // ── ANA LİSTE: backend'den özet satırları çek ──────────────
  // filtre/arama/sayfa değişince yeni istek gönder
  useEffect(() => {
    // İlk sayfa açılışında fullscreen loader göster
    const isFirstLoad = page === 1 && !debouncedQ && scope === "all"
      && selectedUnitId === "allUnits";

    const requestParams = {
      page,
      page_size: PAGE_SIZE,
      ...(debouncedQ ? { q: debouncedQ } : {}),
      ...(scope !== "all" ? { scope } : {}),
      ...(!isPresident && myUnitId
        ? { unit: myUnitId }
        : isPresident && selectedUnitId !== "allUnits"
          ? { unit: selectedUnitId }
          : {}),
    };

    if (isFirstLoad) {
      // İlk açılış: fullscreen loader ile çek (thunk içinden değil, direkt)
      dispatch(fetchRedirectedApplicationsPageThunk(requestParams));
    } else {
      dispatch(fetchRedirectedApplicationsPageThunk(requestParams));
    }
  }, [
    dispatch,
    page,
    pageSize,
    debouncedQ,
    scope,
    selectedUnitId,
    isPresident,
    myUnitId,
  ]);

  // ── Filtre değişince sayfa 1'e dön ─────────────────────────
  useEffect(() => {
    setPage(1);
    setExpanded({});
  }, [debouncedQ, scope, selectedUnitId]);

  // ── Unmount: state temizle ─────────────────────────────────
  useEffect(() => {
    return () => {
      dispatch(clearRedirectedApplications());
      dispatch(clearAllHistory());
    };
  }, [dispatch]);

  // ── Accordion aç/kapa + lazy history ──────────────────────
  const toggleExpand = useCallback((appId) => {
    const id = toStr(appId);
    const willOpen = !expanded[id];

    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

    // Accordion açılıyorsa ve history henüz cache'de yoksa çek
    if (willOpen) {
      const alreadyCached = historyByApplicationId[appId] !== undefined;
      const alreadyLoading = historyLoadingByApplicationId[appId];
      if (!alreadyCached && !alreadyLoading) {
        dispatch(fetchRedirectedApplicationHistoryThunk(appId));
      }
    }
  }, [
    expanded,
    historyByApplicationId,
    historyLoadingByApplicationId,
    dispatch,
  ]);

  const openDetails = useCallback(
    (appId) => {
      // FAZ-22 4b: canonical detay (birim localStorage/scope'tan)
      navigate(`/yonetim/basvurular/${appId}`);
    },
    [navigate]
  );

  // Birim personeli: sadece başvuru hâlâ kendi birimindeyse detaya gidebilir
  const canNavigate = useCallback(
    (item) => {
      if (isPresident) return true;
      return !!myUnitId && sameId(item.current_unit_id, myUnitId);
    },
    [isPresident, myUnitId]
  );

  /* ──────────────────────────────────────────────────────────
     RENDER: Timeline (lazy, accordion içi)
  ────────────────────────────────────────────────────────── */
  const renderTimeline = (appId) => {
    const history = historyByApplicationId[appId];
    const loading = historyLoadingByApplicationId[appId];
    const error   = historyErrorByApplicationId[appId];

    if (loading) {
      return (
        <div className="rb-timeline-loading">
          <span className="rb-timeline-spinner" aria-hidden="true" />
          <span>Geçmiş yükleniyor…</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="rb-empty rb-empty--error">{error}</div>
      );
    }

    if (!history || history.length === 0) {
      return (
        <div className="rb-empty">Bu kayıt için gösterilecek geçmiş yok.</div>
      );
    }

    return (
      <div className="rb-timeline">
        {history.map((log, idx) => (
          <div
            key={log.id || `${appId}-${idx}`}
            className="rb-timeline-item"
          >
            <div className="rb-timeline-left">
              <div className="rb-step">#{idx + 1}</div>
              <div className="rb-time">
                {log.created_at ? <TrDateTime value={log.created_at} /> : "—"}
              </div>
            </div>

            <div className="rb-timeline-main">
              <div className="rb-hop">
                <span className="rb-unit">{log.old_unit_name || "—"}</span>
                <span className="rb-arrow">→</span>
                <span className="rb-unit">{log.new_unit_name || "—"}</span>
              </div>

              <div className="rb-by">
                {log.created_by_name
                  ? titleCaseTr(log.created_by_name)
                  : "Bilinmiyor"}
              </div>

              {log.note ? (
                <div className="rb-note">
                  <div className="rb-note-label">Not</div>
                  <div className="rb-note-text">
                    {ensureFirstUpperTr(log.note)}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    );
  };

  /* ──────────────────────────────────────────────────────────
     RENDER: Tek satır (summary item)
  ────────────────────────────────────────────────────────── */
  const renderRow = (item) => {
    const appId     = item.application_id;
    const isOpen    = !!expanded[toStr(appId)];

    const subjectRaw = safeText(item.subject);
    const subject    = subjectRaw ? ensureFirstUpperTr(subjectRaw) : "(Konu yok)";
    const tracking   = item.tracking_number || "—";

    const hopText    = summaryHopText(item);
    const lastTimeRaw = item.last_forward_at;

    const currentUnitName =
      item.current_unit_name ||
      getUnitName(item.current_unit_id) ||
      "—";

    const navEnabled = canNavigate(item);
    const forwardCount = item.forward_count ?? 0;

    return (
      <div
        key={toStr(appId)}
        className={`rb-row ${navEnabled ? "" : "rb-row--disabled"}`}
      >
        {/* ── Gri başlık (accordion toggle) ─────────────────── */}
        <div
          className="rb-row-head rb-row-head--clickable"
          role="button"
          tabIndex={0}
          onClick={() => toggleExpand(appId)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") toggleExpand(appId);
          }}
        >
          <div className="rb-row-head-left">
            <div className="rb-row-tags">
              <span className="rb-tag">
                <HashtagIcon className="rb-tag-ico" />
                <span>{toStr(appId)}</span>
              </span>

              <span className="rb-tag rb-tag--muted">
                <TagIcon className="rb-tag-ico" />
                <span>Takip: {tracking}</span>
              </span>

              <span className="rb-tag rb-tag--muted">
                <BuildingOffice2Icon className="rb-tag-ico" />
                <span>Mevcut: {currentUnitName}</span>
              </span>

              {forwardCount > 0 ? (
                <span className="rb-tag rb-tag--count">
                  <ArrowPathRoundedSquareIcon className="rb-tag-ico" />
                  <span>{forwardCount}x</span>
                </span>
              ) : null}

              {!navEnabled ? (
                <span className="rb-tag rb-tag--danger">Birim dışı</span>
              ) : !isPresident ? (
                <span className="rb-tag rb-tag--in">Gelen</span>
              ) : null}
            </div>
          </div>

          <div className="rb-row-head-right">
            <ChevronUpDownIcon
              className={`rb-row-chev ${isOpen ? "open" : ""}`}
            />
          </div>
        </div>

        {/* ── Beyaz gövde (konu + meta) ──────────────────────── */}
        <div className="rb-row-body">
          <div className="rb-row-body-main">
            <div className="rb-row-subjectline">
              <button
                type="button"
                className={`rb-row-link ${navEnabled ? "" : "rb-row-link--disabled"}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!navEnabled) return;
                  openDetails(appId);
                }}
                disabled={!navEnabled}
                title={subject}
              >
                {subject}
              </button>

              <button
                type="button"
                className="rb-row-go"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!navEnabled) return;
                  openDetails(appId);
                }}
                disabled={!navEnabled}
                aria-label="Başvuruya git"
                title={
                  navEnabled
                    ? "Başvuruya git"
                    : "Bu başvuru artık biriminizde değil"
                }
              >
                <ArrowTopRightOnSquareIcon className="rb-row-go-ico" />
              </button>
            </div>

            <div className="rb-row-meta">
              <span className="rb-row-meta-label">Son yönlendirme:</span>
              <span className="rb-row-meta-value">{hopText}</span>
              {lastTimeRaw ? (
                <>
                  <span className="rb-row-meta-sep">•</span>
                  <span className="rb-row-meta-time">
                    <TrDateTime value={lastTimeRaw} />
                  </span>
                </>
              ) : null}
              {item.last_forward_by ? (
                <>
                  <span className="rb-row-meta-sep">•</span>
                  <span className="rb-row-meta-by">
                    {titleCaseTr(item.last_forward_by)}
                  </span>
                </>
              ) : null}
            </div>
          </div>
        </div>

        {/* ── Accordion: lazy timeline ───────────────────────── */}
        {isOpen ? (
          <div className="rb-row-expand">
            <div className="rb-row-expand-title">Yönlendirme geçmişi</div>
            {renderTimeline(appId)}
          </div>
        ) : null}
      </div>
    );
  };

  /* ──────────────────────────────────────────────────────────
     RENDER: InfoBox
  ────────────────────────────────────────────────────────── */
  // Bilgi popover içeriği (başlık barındaki "Bilgi" butonuyla açılır)
  const infoContent = isPresident ? (
    <ul className="rb-info-list">
      <li>
        Soldaki <b>Birimler</b> listesinden birim seçerek filtreleyin.{" "}
        <b>Tümü</b> seçilince tüm yönlendirmeler listelenir.
      </li>
      <li>
        <b>Gelen / Giden</b> filtreleri, sadece birim seçtiğinizde aktif olur.
      </li>
      <li>
        Her satırın <b>başlığına</b> tıklayın: yönlendirme geçmişi lazily yüklenir
        ve akordeon gibi açılır/kapanır.
      </li>
      <li>
        Satırın sağındaki <b>↗</b> ikonuna tıklayın: başvuru detayına gidersiniz.
      </li>
    </ul>
  ) : (
    <ul className="rb-info-list">
      <li>
        Bu ekranda yalnızca <b>kendi biriminizle ilgili</b> yönlendirme adımları
        listelenir.
      </li>
      <li>
        Satırın <b>başlığına</b> tıklayın: geçmiş adımlar lazily yüklenir.
      </li>
      <li>
        Başvuru hâlâ biriminizdeyse konu başlığına veya <b>↗</b> ikonuna tıklayıp
        detaya gidebilirsiniz.
      </li>
      <li>
        Biriminize <b>gelen</b> başvurular yeşil <b>"Gelen"</b> etiketiyle,
        biriminizden <b>çıkan</b> başvurular <b>"Birim dışı"</b> etiketiyle
        işaretlenir; bu ekranda <b>ikisi de</b> listelenir. "Birim dışı" olan
        başvurularda detaya giriş pasiftir.
      </li>
    </ul>
  );

  /* ──────────────────────────────────────────────────────────
     RENDER: Scope sekmeleri (Tümü / Gelen / Giden) — header satır 2
  ────────────────────────────────────────────────────────── */
  const scopeTabs = (
    <div
      className="applications-tabs rb-scope-tabs"
      role="tablist"
      aria-label="Yönlendirme filtresi"
    >
      <button
        type="button"
        role="tab"
        aria-selected={scope === "all"}
        className={`applications-tab${scope === "all" ? " active" : ""}`}
        onClick={() => setScope("all")}
      >
        <span className="applications-tab-label">Tümü</span>
      </button>

      <button
        type="button"
        role="tab"
        aria-selected={scope === "incoming"}
        className={`applications-tab${scope === "incoming" ? " active" : ""}`}
        onClick={() => setScope("incoming")}
        disabled={isPresident && selectedUnitId === "allUnits"}
        title={
          isPresident && selectedUnitId === "allUnits" ? "Önce birim seçin" : ""
        }
      >
        <ArrowDownTrayIcon className="applications-tab-icon" />
        <span className="applications-tab-label">Gelen</span>
      </button>

      <button
        type="button"
        role="tab"
        aria-selected={scope === "outgoing"}
        className={`applications-tab${scope === "outgoing" ? " active" : ""}`}
        onClick={() => setScope("outgoing")}
        disabled={isPresident && selectedUnitId === "allUnits"}
        title={
          isPresident && selectedUnitId === "allUnits" ? "Önce birim seçin" : ""
        }
      >
        <ArrowUpTrayIcon className="applications-tab-icon" />
        <span className="applications-tab-label">Giden</span>
      </button>
    </div>
  );

  /* ──────────────────────────────────────────────────────────
     RENDER: Birim listesi (hem masaüstü sidebar hem mobil çekmece)
  ────────────────────────────────────────────────────────── */
  const renderUnitList = (onAfterSelect) => {
    const selectUnit = (val) => {
      setSelectedUnitId(val);
      if (typeof onAfterSelect === "function") onAfterSelect();
    };

    // Stats: items listesinden hesapla (current_unit_id → aktif sayısı)
    const statsById = {};
    for (const item of items) {
      const fromId = toStr(item.current_unit_id);
      if (fromId) {
        if (!statsById[fromId]) statsById[fromId] = { incoming: 0, outgoing: 0 };
        statsById[fromId].incoming += 1;
      }
    }

    return (
      <div className="rb-side-scroll">
        <button
          type="button"
          className={`rb-sideitem ${selectedUnitId === "allUnits" ? "active" : ""}`}
          onClick={() => selectUnit("allUnits")}
          title="Tüm birimler"
        >
          <span className="rb-sideitem-left">
            <span className="rb-sideitem-name">Tüm birimler</span>
          </span>
          <span className="rb-sideitem-right" />
        </button>

        {(units || []).map((u) => {
          const s = statsById[toStr(u.id)] || { incoming: 0, outgoing: 0 };
          return (
            <button
              key={u.id}
              type="button"
              className={`rb-sideitem ${sameId(selectedUnitId, u.id) ? "active" : ""}`}
              onClick={() => selectUnit(toStr(u.id))}
              title={u.name}
            >
              <span className="rb-sideitem-left">
                <span className="rb-sideitem-name">{u.name}</span>
              </span>

              <span className="rb-sideitem-right" aria-label="İstatistik">
                <span className="rb-sidechip" title={`Aktif: ${s.incoming}`}>
                  <ArrowPathRoundedSquareIcon className="rb-sidechip-ico" />
                  <span>{s.incoming}</span>
                </span>
              </span>
            </button>
          );
        })}
      </div>
    );
  };

  /* ──────────────────────────────────────────────────────────
     RENDER: Sidebar (sadece başkan, desktop)
  ────────────────────────────────────────────────────────── */
  const Sidebar = () => {
    if (!isPresident) return null;
    return (
      <aside className="rb-sidebar rb-sticky-sidebar">
        <div className="rb-sidebar-head">
          <div className="rb-sidebar-title">
            <span className="rb-sidebar-title-ico-wrap" aria-hidden="true">
              <BuildingOffice2Icon className="rb-sidebar-title-ico" />
            </span>
            <span>Birimler</span>
          </div>
        </div>
        {renderUnitList()}
      </aside>
    );
  };

  /* ──────────────────────────────────────────────────────────
     RENDER: Content
  ────────────────────────────────────────────────────────── */
  const Content = () => {
    if (listLoading && items.length === 0) {
      return (
        <div className="rb-state">
          <div className="rb-state-title">Yükleniyor…</div>
          <div className="rb-state-sub">Yönlendirme kayıtları getiriliyor.</div>
        </div>
      );
    }

    if (listError) {
      return (
        <div className="rb-state rb-state--error">
          <div className="rb-state-title">Hata</div>
          <div className="rb-state-sub">{listError}</div>
        </div>
      );
    }

    if (!listLoading && items.length === 0) {
      const unitLabel = isPresident
        ? selectedUnitId === "allUnits"
          ? "Tümü"
          : getUnitName(selectedUnitId)
        : getUnitName(myUnitId);

      const scopeLabel =
        scope === "incoming" ? "Gelen" :
        scope === "outgoing" ? "Giden" : "Tümü";

      return (
        <div className="rb-state">
          <div className="rb-state-title">Kayıt yok</div>
          <div className="rb-state-sub">
            <strong>{unitLabel}</strong> • <strong>{scopeLabel}</strong> için sonuç bulunamadı.
          </div>
        </div>
      );
    }

    return (
      <>
        {/* Filtre aktifken içerik bölgesinde hafif overlay */}
        <div className={`rb-list-wrap ${listLoading ? "rb-list-wrap--loading" : ""}`}>
          <div className="rb-list">{items.map(renderRow)}</div>
        </div>

        <Pagination
          page={page}
          totalCount={count}
          pageSize={PAGE_SIZE}
          shownCount={items.length}
          onChange={setPage}
        />
      </>
    );
  };

  /* ──────────────────────────────────────────────────────────
     MAIN RENDER
  ────────────────────────────────────────────────────────── */
  return (
    <div className="rb-page">
      {/* ── STICKY HEADER — paylaşılan PageHeader ─────────────────────────
             Web:   [sekmeler] ........ [arama · birimler · bilgi (sağda)]
             Mobil: (1) başlık + bilgi ikonu  (2) arama + birimler  (3) sekmeler
             stickyRef: header yüksekliği canlı ölçülür → sidebar kaymaz.
             Web'de Tümü/Gelen/Giden hep yerinde; mobilde yalnız birim seçilince
             (rb-tabs-mobile-hidden). ──────────────────────────────────────── */}
      <PageHeader
        title="Yönlendirilenler"
        infoTitle="Nasıl çalışır?"
        infoContent={infoContent}
        className="rb-headbar"
        stickyRef={setStickyEl}
        /* FAZ-97 madde 3 — ŞERİT BURADA DA DURUYOR.
           Kullanıcı: "buraya tıklayınca diğer sekmeler kaybolmasın; sekmelerden
           birine, yeniye tıklayınca da direkt yeni sayfasına geçsin."
           Statü çipi bu sayfada rota DEĞİŞTİRİR (orası ayrı bir sayfa);
           hangi sekmeyle açılacağı rota durumuyla taşınır. */
        tabs={
          <BasvuruCipSeridi
            aktifStatus={null}
            aktifKapsam="yonlendirilen"
            onStatus={(status) =>
              navigate("/yonetim/basvurular", { state: { status } })
            }
            kapsamCipleri={kapsamCipleri}
            onKapsam={() => {}}
          />
        }
        /* Sayfanın KENDİ süzgeci (Tümü / Gelen / Giden) şeridin altında.
           Arama, filtre ve birim seçimi de bu sayfaya ait kalır — kullanıcı
           bunu açıkça istedi. */
        belowToolbar={showScopeTabs ? scopeTabs : null}
      >
        <SearchBox
          value={q}
          onChange={setQ}
          placeholder="Ara: Başvuru ID, takip no, konu…"
        />

        {isPresident ? (
          /* Çıpa kabı: tablette panel bu düğmeden açılır (position: relative).
             Telefonda panel portal ile ekranın altından gelir; kap zararsız. */
          <div className="rb-unitswrap">
            <button
              type="button"
              className="rb-unitsbtn-inline"
              onClick={toggleUnits}
              aria-haspopup="dialog"
              aria-expanded={isUnitsOpen ? "true" : "false"}
            >
              <BuildingOffice2Icon />
              <span>Birimler</span>
            </button>

            {/* FAZ-99 / Y-3 — TABLET: ÇIPALI POPOVER.
                Kullanıcı: "aşağıdan çekmece değil de web'teki filtreler kısmı
                gibi açılsın." `pop-ok` sistemin tek ok kaynağı (FAZ-97 m9):
                panel hangi düğmeden çıktığını kendisi söylüyor. */}
            {!telefon && isUnitsOpen ? (
              <div
                className="rb-unitspop pop-ok"
                data-ok="ust"
                data-ok-x="sag"
                role="dialog"
                aria-label="Birimler"
              >
                <div className="rb-unitspop-head">
                  <span>Birimler</span>
                  <button
                    type="button"
                    className="rb-unitspop-close"
                    onClick={closeUnits}
                    aria-label="Kapat"
                  >
                    <XMarkIcon />
                  </button>
                </div>
                <div className="rb-unitspop-body">{renderUnitList(closeUnits)}</div>
              </div>
            ) : null}
          </div>
        ) : null}
      </PageHeader>

      {/* Başkan TELEFON: Birimler — bilgi penceresiyle AYNI mantık (BottomSheet) */}
      {isPresident && telefon ? (
        <BottomSheet open={isUnitsOpen} onClose={closeUnits} title="Birimler">
          {renderUnitList(closeUnits)}
        </BottomSheet>
      ) : null}

      <div className="page-container rb-container">
        <div
          className={`rb-shell ${
            isPresident && !isMobile ? "rb-shell--withSidebar" : ""
          }`}
        >
          {isPresident && !isMobile ? <Sidebar /> : null}

          <main className="rb-main">
            <Content />
          </main>
        </div>
      </div>
    </div>
  );
};

export default RedirectedApplicationsPage;